import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

/**
 * Bloc réutilisable · L'équipe + Gouvernance + Vision + Le Fil du Midi.
 * Utilisé sur /qui-sommes-nous (page dédiée) et /contact (embarqué sous le formulaire).
 *
 * Équipe : présentation SOBRE — médaillons (initiales), prénom-nom, titre,
 * menu déroulant pour développer le parcours de chacun.
 */

export const TEAM = [
  {
    name: "Kossi Homawoo",
    initials: "KH",
    photo: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/oxwlrvif_Kossi.jpg",
    role: "Fondateur & Directeur artistique · Curateur",
    role_en: "Founder & Artistic Director · Curator",
    body_en: "Founder of Les Arteurs, I imagined the collective as a living space where creation becomes a territory to explore. I carry a structuring vision, coordinate projects, weave links between members and ensure the coherence of our approach. My work mixes writing, staging, exploration of places and collective dynamics.\n\nToday, I am developing Le Fil du Midi as an art trail linking 750 km of territory, 30 years of UNESCO history and 30 contemporary artists per stop. Ultimately, by 2030, the 194 UNESCO member countries will be represented on the Canal du Midi by at least one Arteur.",
    body: "Fondateur des Arteurs, j'ai imaginé le collectif comme un espace vivant où la création devient un territoire à explorer. Je porte une vision structurante, coordonne les projets, tisse des liens entre les membres et veille à la cohérence de notre démarche. Mon travail mêle écriture, mise en scène, exploration des lieux et dynamique collective.\n\nAujourd'hui, je développe Le Fil du Midi comme un parcours artistique qui relie 750 km de territoire, 30 ans d'histoire UNESCO et 30 artistes contemporains par escale. Au final, à l'horizon 2030, les 194 pays membres de l'UNESCO seront représentés sur le Canal du Midi par au moins un Arteur.",
  },
  {
    name: "Sennen Codjo",
    initials: "SC",
    photo: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/m8lf0a88_Georges.webp",
    photoPos: "50% 22%",
    role: "Président Arteurs",
    role_en: "President, Les Arteurs",
    body_en: "Principal registrar at the Centre Pompidou, Sennen brings an essential strategic vision to the collective. He has overseen the installation of major exhibitions around the world (North America, Europe, Asia and the Middle East…) and masters the institutional, logistical and diplomatic challenges of the cultural sector.\n\nA polyglot with a fine knowledge of international networks, he supports the structuring of Les Arteurs and the development of Le Fil du Midi. A childhood friend, he joined the project out of conviction as much as friendship.",
    body: "Régisseur principal au Centre Pompidou, Sennen apporte une vision stratégique essentielle au collectif. Il a supervisé l'implantation d'expositions majeures dans le monde entier (Amérique du Nord, Europe, Asie et Moyen-Orient…) et maîtrise les enjeux institutionnels, logistiques et diplomatiques du secteur culturel.\n\nPolyglotte et fin connaisseur des réseaux internationaux, il accompagne la structuration des Arteurs et le développement du Fil du Midi. Ami d'enfance, il s'est engagé dans le projet par conviction autant que par amitié.",
  },
  {
    name: "Fémi Yaka Djanta Homawoo",
    initials: "FH",
    photo: "/team-femi.jpg",
    role: "Trésorière",
    role_en: "Treasurer",
    body_en: "Fémi handles the collective's financial management with rigour and reliability. Having grown up in an artistic environment — her mother, Charlotte Conrad, is an artist — she has an intuitive understanding of cultural dynamics.\n\nShe ensures the transparency of operations, the monitoring of resources and the coherence between our means and our ambitions. Her presence brings essential stability to Le Fil du Midi.",
    body: "Fémi assure la gestion financière du collectif avec rigueur et fiabilité. Ayant grandi dans un environnement artistique — sa mère, Charlotte Conrad, est artiste — elle possède une compréhension intuitive des dynamiques culturelles.\n\nElle veille à la transparence des opérations, au suivi des ressources et à la cohérence entre nos moyens et nos ambitions. Sa présence apporte une stabilité essentielle au Fil du Midi.",
  },
  {
    name: "Bertrand Ducourau",
    initials: "BD",
    photo: "https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/a4j6rdni_Bertrand%20Ducourau.jpg",
    role: "Membre actif · Commissaire général Canal'Art 2026",
    role_en: "Active member · General Curator, Canal'Art 2026",
    body_en: "Chief heritage curator, Bertrand Ducourau directs the Musée de l'Étang de Thau, the Villa Loupian and the Mediterranean antique garden. Over his career, he has also directed national museums, led major scientific projects and contributed to the vast documentary work that led to the Canal du Midi's UNESCO World Heritage listing in 1996.\n\nFor Canal'Art 2026, he is general curator of the trail and curator of the Loupian stop, where he is imagining a resolutely Mediterranean programme, in which contemporary creations, the memory of places and heritage answer one another.\n\nAnd, as if that were not enough, Bertrand Ducourau also devotes part of his time to his freshly acquired olive grove: a new heritage adventure, this time at the height of trees and olive oil.",
    body: "Conservateur en chef du patrimoine, Bertrand Ducourau dirige le Musée de l'Étang de Thau, la Villa Loupian et le Jardin antique méditerranéen. Au fil de son parcours, il a également dirigé des musées nationaux, porté des projets scientifiques d'envergure et contribué au vaste travail documentaire ayant conduit au classement du Canal du Midi au patrimoine mondial de l'UNESCO, en 1996.\n\nPour Canal'Art 2026, il est commissaire général du parcours et curateur de l'étape de Loupian. Il y imagine une programmation résolument méditerranéenne, où se répondent créations contemporaines, mémoire des lieux et patrimoine.\n\nEt, comme si cela ne suffisait pas, Bertrand Ducourau consacre aussi une partie de son temps à son oliveraie fraîchement acquise : une nouvelle aventure patrimoniale, cette fois à hauteur d'arbre et d'huile d'olive.",
  },
  {
    name: "Michel Digout",
    initials: "MD",
    photo: "/team-michel-digout.jpg",
    role: "Graphiste, réalisateur & mécène de Canal'Art",
    role_en: "Graphic designer, filmmaker & patron of Canal'Art",
    body_en: "A graphic designer and short-film director, Michel has developed a singular visual practice recognised well beyond borders. His films have been selected and shown at numerous international festivals around the world.\n\nA founding member of the association Le Capech à la Côte, he has faithfully supported Canal'Art since its beginnings, as a patron and committed member of the organisation. He notably puts his graphic and technical expertise at the service of the artists, ensuring the quality, compliance and proper handling of the files destined for the exhibition.",
    body: "Graphiste et réalisateur de courts métrages, Michel développe une pratique visuelle singulière, reconnue bien au-delà des frontières. Ses films ont été sélectionnés et présentés dans de nombreux festivals internationaux à travers le monde.\n\nMembre fondateur de l'association Le Capech à la Côte, il soutient Canal'Art avec fidélité depuis ses débuts, en tant que mécène et membre engagé de l'organisation. Il met notamment son expertise graphique et technique au service des artistes, en veillant à la qualité, à la conformité et au bon traitement des fichiers destinés à l'exposition.",
  },
  {
    name: "Pierre Billier",
    initials: "PB",
    photo: "/team-pierre.jpg",
    role: "Membre actif & bénévole",
    role_en: "Active member & volunteer",
    body_en: "A freshly retired engineer, Pierre Billier fell into artist coaching almost by marriage: since wedding an actress and painter, he has supported creative projects with enthusiasm, method and a patience one suspects has been thoroughly tested.",
    body: "Ingénieur fraîchement retraité, Pierre Billier est tombé dans le coaching d'artistes presque par alliance : depuis son mariage avec une comédienne et artiste peintre, il accompagne les projets créatifs avec enthousiasme, méthode et une patience que l'on devine solidement éprouvée.",
  },
  {
    name: "Philippe Maurice",
    initials: "PM",
    photo: "/team-doc-maurice.jpg",
    role: "Membre actif & bénévole",
    role_en: "Active member & volunteer",
    body_en: "Having fallen into art as a child, Philippe Maurice never quite recovered. A doctor by day, an artist whenever an idea crosses his mind and a goat breeder between two inspirations, he practises multi-activism with delight. His goats, for their part, actively contribute to the adventure by producing cheeses that rank among the most exquisite in Haute-Garonne.",
    body: "Tombé dans l'art quand il était petit, Philippe Maurice ne s'en est jamais tout à fait remis. Médecin le jour, artiste dès qu'une idée lui traverse l'esprit et éleveur de chèvres entre deux inspirations, il pratique le multi-activisme avec bonheur. Ses chèvres, elles, participent activement à l'aventure en produisant des fromages qui comptent parmi les plus exquis de Haute-Garonne.",
  },
];

function ComiteListeRequestForm() {
  const { lang } = useLang();
  const en = lang === "en";
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", organization: "", reason: "" });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/comite/liste-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setDone(true);
    } catch {
      setError(en ? "An error occurred. Please try again." : "Une erreur est survenue. Merci de réessayer.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <p className="mt-4 text-[15px] text-fg/80 leading-relaxed" data-testid="comite-request-success">
        {en ? "Thank you, your request has been recorded. The list will be sent to you by email." : "Merci, votre demande a bien été enregistrée. La liste vous sera transmise par email."}
      </p>
    );
  }

  return (
    <div className="mt-4">
      <p className="text-[15px] text-fg/80 leading-relaxed">
        {en ? "The full list of the selection committee is available " : "La liste complète du comité de sélection est disponible "}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="underline decoration-cinabre underline-offset-4 text-cinabre hover:text-fg transition-colors"
          data-testid="comite-request-toggle"
        >
          {en ? "upon simple request here" : "sur simple demande ici"}
        </button>.
      </p>
      <AnimatePresence initial={false}>
        {open && (
          <motion.form
            onSubmit={submit}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.65, 0, 0.35, 1] }}
            className="overflow-hidden"
            data-testid="comite-request-form"
          >
            <div className="mt-5 space-y-3">
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={en ? "Your name *" : "Votre nom *"}
                className="w-full bg-bg border border-fg/20 px-4 py-2.5 text-[15px] focus:outline-none focus:border-cinabre transition-colors"
                data-testid="comite-request-name"
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={en ? "Your email *" : "Votre email *"}
                className="w-full bg-bg border border-fg/20 px-4 py-2.5 text-[15px] focus:outline-none focus:border-cinabre transition-colors"
                data-testid="comite-request-email"
              />
              <input
                value={form.organization}
                onChange={(e) => setForm({ ...form, organization: e.target.value })}
                placeholder={en ? "Organisation / capacity" : "Organisme / qualité"}
                className="w-full bg-bg border border-fg/20 px-4 py-2.5 text-[15px] focus:outline-none focus:border-cinabre transition-colors"
                data-testid="comite-request-organization"
              />
              <textarea
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder={en ? "Reason for the request" : "Motif de la demande"}
                rows={2}
                className="w-full bg-bg border border-fg/20 px-4 py-2.5 text-[15px] focus:outline-none focus:border-cinabre transition-colors resize-none"
                data-testid="comite-request-reason"
              />
              {error && <p className="text-cinabre text-sm">{error}</p>}
              <button
                type="submit"
                disabled={sending}
                className="bg-cinabre text-bg px-6 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-fg transition-colors disabled:opacity-50"
                data-testid="comite-request-submit"
              >
                {sending ? (en ? "Sending…" : "Envoi…") : (en ? "Send the request" : "Envoyer la demande")}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}

const VISION_LINES = {
  fr: [
    "Relier les territoires, les artistes et les publics.",
    "Faire dialoguer patrimoine et création.",
    "Que l'art circule, respire, transforme.",
  ],
  en: [
    "Connecting territories, artists and audiences.",
    "Bringing heritage and creation into dialogue.",
    "Letting art circulate, breathe, transform.",
  ],
};

function TeamMedallion({ m, i, compact }) {
  const { lang } = useLang();
  const en = lang === "en";
  const [open, setOpen] = useState(false);
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: i * 0.04 }}
      className="border border-fg/12 bg-bg hover:border-cinabre/40 transition-colors duration-500"
      data-testid={`team-${i}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`team-parcours-${i}`}
        className="w-full flex items-center gap-5 md:gap-6 p-5 md:p-6 text-left group"
        data-testid={`team-toggle-${i}`}
      >
        {/* Médaillon photo ou initiales */}
        {m.photo ? (
          <div
            className={`shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-full overflow-hidden ${m.accent ? "ring-2 ring-cinabre ring-offset-2 ring-offset-bg" : "border border-fg/15"}`}
            aria-hidden="true"
          >
            <img
              src={m.photo}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover"
              style={m.photoPos ? { objectPosition: m.photoPos } : undefined}
            />
          </div>
        ) : (
          <div
            className={`shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center font-serif text-lg md:text-xl leading-none tracking-tight ${
              m.accent
                ? "bg-cinabre text-bg"
                : "bg-fg/[0.06] text-fg/75 border border-fg/15"
            }`}
            aria-hidden="true"
          >
            {m.initials}
          </div>
        )}

        {/* Nom + titre */}
        <div className="flex-1 min-w-0">
          <h3 className="font-serif font-light text-xl md:text-2xl leading-tight tracking-tight truncate">
            {m.name}
          </h3>
          <div className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-fg/55">
            {en && m.role_en ? m.role_en : m.role}
          </div>
        </div>

        {/* Chevron */}
        <ChevronDown
          className={`shrink-0 w-5 h-5 text-fg/60 transition-transform duration-500 ${open ? "rotate-180 text-cinabre" : ""}`}
          aria-hidden="true"
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`team-parcours-${i}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.65, 0, 0.35, 1] }}
            className="overflow-hidden border-t border-fg/12"
          >
            <div className="px-5 md:px-6 py-5 md:py-6 md:pl-[6.25rem]">
              {(en && m.body_en ? m.body_en : m.body).split("\n\n").map((p, j) => (
                <p key={j} className={`text-fg/80 leading-relaxed ${compact ? "text-[15px]" : "text-base md:text-[17px]"} ${j > 0 ? "mt-4" : ""}`}>
                  {p}
                </p>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}

export default function AboutContent({ compact = false }) {
  const { lang } = useLang();
  const en = lang === "en";
  return (
    <>
      {/* L'équipe — médaillons + accordéon */}
      <section className={compact ? "mt-16 md:mt-20" : "mt-24 md:mt-32"} data-testid="team-section">
        <div className="meta-label text-cinabre">§ {en ? "The team" : "L'équipe"}</div>
        <h2 className="mt-4 font-serif font-light text-4xl md:text-6xl leading-[0.95] tracking-tight">
          {en ? <>Seven <em className="italic">hands</em>. One thread.</> : <>Sept <em className="italic">mains</em>. Un fil.</>}
        </h2>
        <p className="mt-6 max-w-2xl text-fg/70 leading-relaxed">
          {en ? <>Founders, active members, volunteers — seven journeys that make up Le Fil du Midi.{" "}<span className="text-fg/55">Click to expand each journey.</span></> : <>Fondateurs, membres actifs, bénévoles — sept parcours qui composent Le Fil du Midi.{" "}<span className="text-fg/55">Cliquez pour développer chaque parcours.</span></>}
        </p>

        <div className="mt-10 md:mt-12 grid gap-3 md:gap-4 max-w-4xl">
          {TEAM.map((m, i) => (
            <TeamMedallion key={m.name} m={m} i={i} compact={compact} />
          ))}
        </div>
      </section>

      {/* Gouvernance · rôles institutionnels selon les statuts */}
      <section className="mt-20 md:mt-24" data-testid="governance-section">
        <div className="meta-label boxed text-cinabre">§ {en ? "Governance" : "Gouvernance"}</div>
        <h2 className="mt-6 font-serif font-light text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-3xl">
          {en ? <>The roles, <em className="italic">as per the statutes</em>.</> : <>Les rôles, <em className="italic">selon les statuts</em>.</>}
        </h2>

        <div className="mt-12 grid md:grid-cols-3 gap-6 md:gap-8">
          {/* Membres fondateurs */}
          <div className="border border-fg/15 p-8 md:p-10" data-testid="governance-fondateurs">
            <div className="meta-label text-cinabre">§ {en ? "Founding members" : "Membres fondateurs"}</div>
            <div className="mt-4 font-mono text-[10px] tracking-[0.2em] uppercase text-fg/55">{en ? "Signatories of the statutes" : "Signataires des statuts"}</div>
            <ul className="mt-5 space-y-3 text-fg/85 leading-snug">
              <li><strong>Kossi Homawoo</strong> — {en ? "General Secretary" : "Secrétaire général"}</li>
              <li><strong>Sennen Codjo</strong> — {en ? "President" : "Président"}</li>
              <li><strong>Fémi Yaka Djanta Homawoo</strong> — {en ? "Treasurer" : "Trésorière"}</li>
            </ul>
          </div>

          {/* Membres actifs · Commissaires */}
          <div className="border border-fg/15 p-8 md:p-10" data-testid="governance-commissaires">
            <div className="meta-label text-cinabre">§ {en ? "Active members" : "Membres actifs"}</div>
            <div className="mt-4 font-mono text-[10px] tracking-[0.2em] uppercase text-fg/55">{en ? "Exhibition curators" : "Commissaires d'expositions"}</div>
            <ul className="mt-5 space-y-3 text-fg/85 leading-snug">
              <li><strong>Bertrand Ducourau</strong> — {en ? "General Curator · Loupian" : "Commissaire général · Loupian"}</li>
              <li><strong>Sennen Codjo</strong> — {en ? "Curator" : "Commissaire"}</li>
              <li><strong>Kossi Homawoo</strong> — {en ? "Head of the Fil du Midi project" : "Chef du projet Fil du Midi"}</li>
            </ul>
          </div>

          {/* Comité de sélection */}
          <div className="border-l-2 border-cinabre bg-cinabre/[0.03] p-8 md:p-10" data-testid="governance-comite">
            <div className="meta-label text-cinabre">§ {en ? "Selection committee" : "Comité de sélection"}</div>
            <div className="mt-4 font-mono text-[10px] tracking-[0.2em] uppercase text-fg/55">{en ? "Curatorial selection of artworks" : "Sélection curatoriale des œuvres"}</div>
            <p className="mt-5 text-fg/85 leading-relaxed text-[15px]">
              {en ? <>The selection committee is composed of the <strong>founding members</strong>, the <strong>active members</strong>, representatives of <strong>Voies Navigables de France (VNF)</strong>, representatives of the <strong>Prefect of Occitanie</strong> and one representative of the <strong>town hall of each host town</strong>.</> : <>Le comité de sélection est composé des <strong>membres fondateurs</strong>, des <strong>membres actifs</strong>, de représentants de <strong>Voies Navigables de France (VNF)</strong>, de représentants du <strong>préfet d'Occitanie</strong> et d'un représentant de la <strong>mairie des villes hôtes</strong>.</>}
            </p>
            <div className="mt-6 pt-5 border-t border-cinabre/25">
              <div className="meta-label text-cinabre">§ {en ? "President of the 2026 selection committee" : "Président du comité de sélection 2026"}</div>
              <div className="mt-2 font-serif text-xl leading-tight"><strong>Sennen Codjo</strong></div>
              <div className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-fg/55">{en ? "President of Arteurs Org" : "Président de Arteurs Org"}</div>
            </div>
            <div className="mt-6 pt-5 border-t border-cinabre/25">
              <ComiteListeRequestForm />
            </div>
          </div>
        </div>
      </section>

      {/* Notre vision */}
      <section className="mt-20 md:mt-24">
        <div className="meta-label text-cinabre">§ {en ? "Our vision" : "Notre vision"}</div>
        <h2 className="mt-4 font-serif font-light text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-4xl">
          {en ? <>We break down walls to better <em className="italic">open the doors</em> of galleries and museums.</> : <>Nous cassons les murs pour mieux <em className="italic">ouvrir les portes</em> des galeries et des musées.</>}
        </h2>

        <ul className="mt-10 space-y-4 max-w-3xl">
          {VISION_LINES[lang].map((line, i) => (
            <li key={i} className="flex items-start gap-4 text-fg/85 leading-relaxed text-lg" data-testid={`vision-line-${i}`}>
              <span className="text-cinabre text-2xl leading-none pt-1">·</span>
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Le Fil du Midi — premier grand chantier · texte institutionnel VNF + SGAR Occitanie */}
      <section className="mt-20 md:mt-24">
        <div className="border-l-2 border-cinabre pl-6 md:pl-10 py-4">
          <div className="meta-label text-cinabre">§ Le Fil du Midi</div>
          <h2 className="mt-4 font-serif font-light text-3xl md:text-5xl leading-[1] tracking-tight max-w-3xl">
            Notre premier grand chantier.
          </h2>
          <p className={`mt-6 max-w-3xl text-fg/80 leading-relaxed ${compact ? "" : "text-lg"}`}>
            Le Fil du Midi constitue le premier grand chantier porté par <strong>Les Arteurs</strong>. À terme, il a vocation à relier <strong>750 km de territoire</strong> et à s'inscrire dans une dynamique internationale associant les <strong>194 pays membres de l'UNESCO</strong>, afin de devenir <em className="italic">le plus vaste parcours artistique au monde.</em>
          </p>
          <p className="mt-5 max-w-3xl text-fg/80 leading-relaxed">
            L'édition <strong>2026</strong> marque une étape fondatrice : la célébration des <strong>30 ans de l'inscription du Canal du Midi au patrimoine mondial de l'UNESCO</strong>. Pour cette première édition, le Fil du Midi se structure autour de <strong>6 escales</strong>, chacune confiée à un commissaire, et d'une programmation de <strong>30 artistes par escale</strong>.
          </p>

          {/* Encadré institutionnel VNF + Préfecture Occitanie */}
          <div className="mt-8 max-w-3xl bg-fg/[0.03] border border-fg/12 p-6 md:p-8" data-testid="fil-institutional">
            <div className="meta-label text-fg/55 mb-4">§ Portage institutionnel</div>
            <p className="text-fg/80 leading-relaxed">
              Cette édition est portée institutionnellement par <strong>Voies Navigables de France (VNF)</strong> et le <strong>SGAR Occitanie</strong>, dans le cadre de la mission nationale dédiée aux 30 ans UNESCO. Leur soutien garantit l'ancrage du projet dans une <em className="italic">politique publique de valorisation du patrimoine, de développement territorial et de rayonnement culturel.</em>
            </p>

            {/* Logos institutionnels */}
            <div className="mt-7 pt-6 border-t border-fg/12 flex flex-wrap items-center gap-8 md:gap-12">
              <a href="https://www.vnf.fr" target="_blank" rel="noreferrer" className="block group" data-testid="logo-vnf" aria-label="Voies Navigables de France">
                <img
                  src="https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/vwx7ismp_VOIES%20NAVIGABLES%20DE%20FRANCE%20.png"
                  alt="Voies Navigables de France"
                  className="h-14 md:h-16 w-auto object-contain opacity-85 group-hover:opacity-100 transition-opacity"
                  loading="lazy"
                />
              </a>

              <a href="https://www.prefectures-regions.gouv.fr/occitanie" target="_blank" rel="noreferrer"
                 className="block group border-l border-fg/20 pl-8" data-testid="logo-prefet-occitanie" aria-label="Préfecture de la Région Occitanie">
                <img
                  src="https://customer-assets-m6fa6gv7.emergentagent.net/job_midi-gallery/artifacts/tvfubbxt_Logo%20PREF_region_Occitanie_RVB.webp"
                  alt="Préfet de la Région Occitanie · SGAR"
                  className="h-16 md:h-20 w-auto object-contain opacity-90 group-hover:opacity-100 transition-opacity"
                  loading="lazy"
                />
              </a>
            </div>
            <p className="mt-4 font-mono text-[9px] tracking-[0.22em] uppercase text-fg/45">
              Cliquez sur les logos pour accéder aux sites officiels
            </p>
          </div>
        </div>
      </section>

      {/* Manifeste de clôture — Le Fil du Midi · typographie éditoriale grand format */}
      <section className="mt-24 md:mt-32 relative" data-testid="fil-manifesto">
        {/* Ligne rouge décorative en haut à gauche */}
        <div className="absolute top-0 left-0 w-16 md:w-24 h-[2px] bg-cinabre" aria-hidden="true" />

        <div className="pt-10 md:pt-14">
          <div className="meta-label boxed text-cinabre">§ Ce que nous portons</div>

          {/* Paragraphe 1 · préfiguration — gros format italique */}
          <p className="mt-10 md:mt-14 font-serif italic font-light text-3xl md:text-5xl leading-[1.15] tracking-tight text-fg/90 max-w-6xl">
            Ce premier Fil est une <span className="text-cinabre not-italic font-normal">préfiguration</span> — une ligne artistique qui traverse les paysages, les mémoires, les gestes et les voix, et qui ouvre un <em>dialogue entre patrimoine et création contemporaine.</em>
          </p>

          {/* Paragraphe 2 · l'Atlantique à la Méditerranée — capitales éditoriales */}
          <div className="mt-14 md:mt-20 grid md:grid-cols-12 gap-6 md:gap-10 items-start">
            <div className="md:col-span-2 pt-4">
              <div className="w-full h-[1px] bg-fg/40" aria-hidden="true" />
            </div>
            <p className="md:col-span-10 font-serif font-light text-2xl md:text-4xl leading-[1.2] tracking-tight text-fg/85">
              Une ligne qui relie{" "}
              <span className="font-mono text-base md:text-lg tracking-[0.2em] uppercase align-middle text-cinabre">l'Atlantique</span>
              {" "}à la{" "}
              <span className="font-mono text-base md:text-lg tracking-[0.2em] uppercase align-middle text-cinabre">Méditerranée</span>,<br className="hidden md:block"/>
              <em className="italic text-fg/70">l'histoire à l'avenir, les territoires aux artistes, les habitants aux œuvres.</em>
            </p>
          </div>

          {/* Paragraphe 3 · dans les pas de Riquet + canal-du-midi.com */}
          <div className="mt-16 md:mt-24 bg-fg/[0.04] border-l-4 border-cinabre p-8 md:p-12 max-w-5xl">
            <div className="meta-label text-fg/55 mb-6">§ Dans les pas de Riquet</div>
            <p className="font-serif text-xl md:text-2xl leading-[1.45] tracking-tight text-fg/85">
              Le projet s'inscrit explicitement <strong className="font-medium text-fg">dans les pas de Pierre-Paul Riquet</strong>, dont l'ambition initiale fut de relier <em className="italic">deux mers, deux mondes et deux économies</em> par un trait d'ingénierie visionnaire.
            </p>
            <p className="mt-6 text-fg/75 leading-relaxed">
              Pour tout savoir sur l'histoire, les ouvrages et la gouvernance du canal —{" "}
              <a
                href="https://www.canal-du-midi.com"
                target="_blank"
                rel="noreferrer"
                className="text-cinabre underline decoration-cinabre/40 decoration-1 underline-offset-4 hover:decoration-cinabre transition-colors font-medium"
              >
                canal-du-midi.com
              </a>
              , le site institutionnel de référence.
            </p>
          </div>

          {/* Paragraphe 4 · trait d'art — climax éditorial */}
          <div className="mt-16 md:mt-24 text-center max-w-5xl mx-auto">
            <p className="font-serif italic font-light text-4xl md:text-6xl leading-[1.05] tracking-tight text-fg">
              Le Fil du Midi prolonge ce geste fondateur —
            </p>
            <p className="mt-6 md:mt-8 font-serif text-2xl md:text-4xl leading-[1.2] tracking-tight text-cinabre">
              un <strong className="font-normal">trait d'art</strong> qui relie les sensibilités,<br/>les cultures et les horizons.
            </p>
            <p className="mt-8 max-w-2xl mx-auto text-fg/70 leading-relaxed">
              …en faisant du Canal du Midi un espace de <em className="italic">création, de circulation et de transmission.</em>
            </p>
          </div>

          {/* CTAs — nous rejoindre · mécénat · parcours */}
          <div className="mt-16 md:mt-24 flex flex-wrap items-center justify-center gap-3 md:gap-4" data-testid="fil-manifesto-ctas">
            <a
              href="/rejoindre"
              className="group inline-flex items-center gap-3 px-7 py-4 bg-fg text-bg font-mono text-[11px] tracking-[0.24em] uppercase hover:bg-cinabre transition-colors duration-500"
              data-testid="fil-cta-rejoindre"
            >
              <span>Je participe</span>
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </a>
            <a
              href="/mecenat"
              className="group inline-flex items-center gap-3 px-7 py-4 border border-fg/25 font-mono text-[11px] tracking-[0.24em] uppercase text-fg hover:border-cinabre hover:text-cinabre transition-colors duration-500"
              data-testid="fil-cta-mecenat"
            >
              <span>Devenir mécène</span>
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </a>
            <a
              href="/parcours"
              className="group inline-flex items-center gap-3 px-7 py-4 border border-fg/25 font-mono text-[11px] tracking-[0.24em] uppercase text-fg hover:border-cinabre hover:text-cinabre transition-colors duration-500"
              data-testid="fil-cta-parcours"
            >
              <span>Découvrir le parcours</span>
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
